import React, { FC, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useScript } from 'hoofd'
import { formatInTimeZone } from 'date-fns-tz'

import { remark } from 'remark'
import strip from 'strip-markdown'

import { Metadata } from '../Metadata'
import { buildBreadcrumbList, HOME_BREADCRUMB } from '../buildBreadcrumbList'

import { EventMetadata_event$key } from './__generated__/EventMetadata_event.graphql'

import { HOST_NAME } from '@data/config/url'
import { EMOJI_REGEX } from '@data/config/regex'
import { OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT } from '@data/config/socialCard'

const deriveAvailability = (tier: {
  status: string
  ticketLimit: number | null | undefined
  soldCount: number | null | undefined
  availableFrom: string | null | undefined
  availableUntil: string | null | undefined
}): string => {
  if (tier.status === 'paused') return 'https://schema.org/SoldOut'
  if (tier.ticketLimit && tier.soldCount && tier.soldCount >= tier.ticketLimit)
    return 'https://schema.org/SoldOut'
  if (tier.availableUntil && new Date() > new Date(tier.availableUntil))
    return 'https://schema.org/SoldOut'
  if (tier.availableFrom && new Date() < new Date(tier.availableFrom))
    return 'https://schema.org/PreOrder'
  return 'https://schema.org/InStock'
}

export const EventMetadata: FC<{
  event: EventMetadata_event$key
  title?: string
  description?: string
}> = ({ event, title, description }) => {
  const data = useFragment(
    graphql`
      fragment EventMetadata_event on Event {
        name
        prettyUrl
        description
        coverPhoto {
          seoUrl: transformUrl(
            transform: { w: 1200, h: 630, dpr: 1, fit: "cover" }
          )
        }

        startAt
        endAt
        timeZone

        hasExternalUrl
        hasVenue

        externalUrl
        seoVenue {
          name
          address {
            addressLine1
            addressLine2
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode
          }
        }

        updatedAt
        createdAt
        attendeeLimit

        owner {
          __typename

          ... on User {
            firstName
            lastName
            slugId

            isMe
            primaryPhoto {
              seoUrl: transformUrl(
                transform: { w: 500, h: 500, dpr: 1, fit: "contain" }
              )
            }
          }

          ... on Guild {
            name
            slugId

            myMembership {
              membershipLevel
            }
            primaryPhoto {
              seoUrl: transformUrl(
                transform: { w: 500, h: 500, dpr: 1, fit: "contain" }
              )
            }
          }
        }

        seoPresentations: presentations(first: 10) {
          edges {
            node {
              title
              prettyUrl

              description

              presenter {
                slugId
                firstName
                lastName

                primaryPhoto {
                  seoUrl: transformUrl(
                    transform: { w: 500, h: 500, dpr: 1, fit: "contain" }
                  )
                }
              }

              presenterFirstName
              presenterLastName
            }
          }
        }

        seoTicketingTiers: eventTicketingTiers(
          filter: { status: { notEqualTo: "hidden" } }
        ) {
          edges {
            node {
              name
              price
              currency
              status
              availableFrom
              availableUntil
              ticketLimit
              soldCount
            }
          }
        }
      }
    `,
    event
  )

  const strippedDescription = useMemo(() => {
    const raw = description ? description : data.description
    if (!raw) return raw

    return String(
      remark()
        // @ts-expect-error strip-markdown types are incompatible with remark
        .use(strip)
        .processSync(raw)
    ).replace(/\\([\\*_~`[](){}#+.!|>-])/g, '$1')
  }, [description, data.description])

  const dateTime = useMemo(
    () =>
      formatInTimeZone(new Date(data.startAt), data.timeZone, 'MMM do h:mma'),
    [data.startAt, data.timeZone]
  )

  const dateTimeDescription = useMemo(
    () =>
      strippedDescription
        ? `${dateTime}: ${strippedDescription.replace(EMOJI_REGEX, '')}`
        : dateTime,
    [dateTime, strippedDescription]
  )

  const structuredData = useMemo(() => {
    const venue = data.hasVenue
      ? {
          '@type': 'Place',
          name: data.seoVenue?.name,
          telephone: '', // needed for a Safari bug...
          address: {
            '@type': 'PostalAddress',
            streetAddress: data.seoVenue?.address?.addressLine1,
            addressLocality: data.seoVenue?.address?.city,
            postalCode: data.seoVenue?.address?.zipPostalCode,
            addressRegion: data.seoVenue?.address?.stateProvinceRegionCode,
            addressCountry: data.seoVenue?.address?.countryCode,
          },
        }
      : null

    const performer = data.seoPresentations.edges.map((edge) => {
      if (edge.node.presenter) {
        return {
          '@type': 'Person',
          name: `${edge.node.presenter.firstName} ${edge.node.presenter.lastName}`,
          url: `${HOST_NAME}/${edge.node.presenter.slugId || ''}`,
          image: edge.node.presenter.primaryPhoto?.seoUrl || null,
        }
      }

      return {
        '@type': 'Person',
        name: `${edge.node.presenterFirstName} ${edge.node.presenterLastName}`,
      }
    })

    const tiers = data.seoTicketingTiers.edges
      .map((e) => e.node)
      .filter((node): node is NonNullable<typeof node> => node != null)

    const offers =
      tiers.length > 0
        ? tiers.map((tier) => ({
            '@type': 'Offer' as const,
            name: tier.name,
            price: (tier.price / 100).toFixed(2),
            priceCurrency: tier.currency.toUpperCase(),
            url: `${HOST_NAME}/events/${data.prettyUrl}`,
            availability: deriveAvailability(tier),
            validFrom: tier.availableFrom || data.createdAt,
          }))
        : [
            {
              '@type': 'Offer' as const,
              name: 'Free',
              price: '0',
              priceCurrency: 'USD',
              url: `${HOST_NAME}/events/${data.prettyUrl}`,
              availability: 'https://schema.org/InStock',
              validFrom: data.createdAt,
            },
          ]

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: data.name,
      description: strippedDescription,
      url: `${HOST_NAME}/events/${data.prettyUrl}`,
      organizer:
        data.owner?.__typename === 'User'
          ? {
              '@type': 'Person',
              givenName: data.owner.firstName,
              familyName: data.owner.lastName,
              name: `${data.owner.firstName} ${data.owner.lastName}`,
              url: `${HOST_NAME}/${data.owner?.slugId || ''}`,
              image: data.owner.primaryPhoto?.seoUrl || null,
            }
          : data.owner?.__typename === 'Guild'
            ? {
                '@type': 'Organization',
                name: data.owner.name || 'Guild',
                url: `${HOST_NAME}/${data.owner.slugId || ''}`,
                logo: data.owner.primaryPhoto?.seoUrl || null,
              }
            : null,
      startDate: data.startAt,
      endDate: data.endAt,
      eventAttendanceMode:
        data.hasExternalUrl && data.hasVenue
          ? 'https://schema.org/MixedEventAttendanceMode'
          : data.hasVenue
            ? 'https://schema.org/OfflineEventAttendanceMode'
            : 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location:
        data.hasExternalUrl && data.hasVenue
          ? [
              {
                '@type': 'VirtualLocation',
                url:
                  data.externalUrl || `${HOST_NAME}/events/${data.prettyUrl}`,
              },
              venue,
            ]
          : data.hasVenue
            ? venue
            : {
                '@type': 'VirtualLocation',
                url:
                  data.externalUrl || `${HOST_NAME}/events/${data.prettyUrl}`,
              },
      image: [
        `${HOST_NAME}/social/event/${data.prettyUrl}/card.png?${data.updatedAt}`,
        data.coverPhoto?.seoUrl,
      ].filter(Boolean),
      ...(performer.length > 0 ? { performer } : {}),
      ...(data.attendeeLimit
        ? { maximumAttendeeCapacity: data.attendeeLimit }
        : {}),
      offers,
    })
  }, [data, strippedDescription])

  useScript({
    type: 'application/ld+json',
    text: structuredData,
  })

  const breadcrumbData = useMemo(() => {
    const eventUrl = `${HOST_NAME}/events/${data.prettyUrl}`

    if (data.owner?.__typename === 'Guild') {
      return buildBreadcrumbList([
        HOME_BREADCRUMB,
        {
          name: data.owner.name || 'Guild',
          url: `${HOST_NAME}/${data.owner.slugId}`,
        },
        { name: data.name, url: eventUrl },
      ])
    }

    return buildBreadcrumbList([
      HOME_BREADCRUMB,
      { name: 'Events', url: `${HOST_NAME}/events` },
      { name: data.name, url: eventUrl },
    ])
  }, [data])

  useScript({ type: 'application/ld+json', text: breadcrumbData })

  return (
    <Metadata
      canonicalUrl={`${HOST_NAME}/events/${data.prettyUrl}`}
      description={description || dateTimeDescription}
      imageTransformParams='NONE'
      imageWidth={OG_IMAGE_WIDTH}
      imageHeight={OG_IMAGE_HEIGHT}
      ogType='event'
      photoUrl={`${HOST_NAME}/social/event/${data.prettyUrl}/card.png?${data.updatedAt}`}
      title={title || data.name}
    />
  )
}
